import React, { useState, useEffect, useRef } from 'react'
import { Box, Typography, IconButton, Paper, Popper, alpha, useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'

const TutorialPinpoint = ({ children, storageKey, placement = 'right' }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [visible, setVisible] = useState(false)
  const anchorRef = useRef(null)

  useEffect(() => {
    const dismissed = localStorage.getItem(storageKey)
    if (!dismissed) {
      setVisible(true)
    }
  }, [storageKey])

  const handleDismiss = (e) => {
    e.stopPropagation()
    e.preventDefault()
    localStorage.setItem(storageKey, 'true')
    setVisible(false)
  }

  const handleChildClick = () => {
    if (visible) {
      localStorage.setItem(storageKey, 'true')
      setVisible(false)
    }
  }

  const isLeft = placement === 'left'

  const caretStyle = isLeft
    ? {
        right: -7,
        left: 'auto',
        borderLeft: `7px solid ${theme.palette.background.paper}`,
        borderRight: 'none',
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
      }
    : {
        left: -7,
        right: 'auto',
        borderRight: `7px solid ${theme.palette.background.paper}`,
        borderLeft: 'none',
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
      }

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      {/* Pulsing ring 1 */}
      {visible && (
        <Box
          sx={{
            position: 'absolute',
            inset: -6,
            borderRadius: '50%',
            border: `2px solid ${theme.palette.primary.main}`,
            opacity: 0,
            animation: 'pinpointPulse 2s ease-out infinite',
            '@keyframes pinpointPulse': {
              '0%': { transform: 'scale(0.9)', opacity: 0.8 },
              '70%': { transform: 'scale(1.7)', opacity: 0 },
              '100%': { transform: 'scale(1.7)', opacity: 0 },
            },
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}

      {/* Pulsing ring 2 — staggered */}
      {visible && (
        <Box
          sx={{
            position: 'absolute',
            inset: -6,
            borderRadius: '50%',
            border: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
            opacity: 0,
            animation: 'pinpointPulse 2s ease-out 0.65s infinite',
            '@keyframes pinpointPulse': {
              '0%': { transform: 'scale(0.9)', opacity: 0.7 },
              '70%': { transform: 'scale(1.7)', opacity: 0 },
              '100%': { transform: 'scale(1.7)', opacity: 0 },
            },
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}

      {/* Children (the actual icon button) */}
      <Box ref={anchorRef} sx={{ position: 'relative', zIndex: 1 }} onClick={handleChildClick}>
        {children}
      </Box>

      {/* Floating tooltip card — portaled via Popper so it isn't clipped by sidebars/drawers */}
      <Popper
        open={visible}
        anchorEl={anchorRef.current}
        placement={isLeft ? 'left' : 'right'}
        modifiers={[{ name: 'offset', options: { offset: [0, 14] } }]}
        style={{ zIndex: 1400 }}
      >
        <Paper
          elevation={4}
          sx={{
            position: 'relative',
            width: 230,
            p: '10px 10px 10px 12px',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            boxShadow: `0 6px 24px ${alpha(theme.palette.common.black, 0.12)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.08)}`,
            animation: 'pinpointCardIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
            '@keyframes pinpointCardIn': {
              '0%': {
                opacity: 0,
                transform: `translateX(${isLeft ? '8px' : '-8px'})`,
              },
              '100%': {
                opacity: 1,
                transform: 'translateX(0)',
              },
            },
            // Arrow caret
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              ...caretStyle,
            },
          }}
        >
          {/* Accent dot */}
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: theme.palette.primary.main,
              flexShrink: 0,
              mt: '4px',
              animation: 'pinpointDotPulse 1.8s ease-in-out infinite',
              '@keyframes pinpointDotPulse': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.5, transform: 'scale(0.75)' },
              },
            }}
          />
          <Typography
            sx={{
              flex: 1,
              fontSize: '0.775rem',
              lineHeight: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
            }}
          >
            {t('tutorial_pinpoint_text')}
          </Typography>
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              p: '2px',
              ml: 0.25,
              flexShrink: 0,
              color: theme.palette.text.disabled,
              alignSelf: 'flex-start',
              borderRadius: '6px',
              '&:hover': {
                color: theme.palette.text.primary,
                bgcolor: alpha(theme.palette.text.primary, 0.06),
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Paper>
      </Popper>
    </Box>
  )
}

export default TutorialPinpoint
